// Page 1 of the RB Restoration Service Agreement.
// Mirrors the printed form layout: header, homeowner info, contract scope,
// specified services with checkboxes, terms paragraphs, and signatures.
import { SignaturePad } from "@/components/forms/SignaturePad";
import { InlineField, CheckRow, SectionTitle } from "./AgreementFields";
import type { RBAgreementData } from "./agreementTypes";

interface Props {
  data: RBAgreementData;
  update: <K extends keyof RBAgreementData>(k: K, v: RBAgreementData[K]) => void;
  logoUrl: string | null;
  readOnly?: boolean;
}

export function AgreementPage1({ data, update, logoUrl }: Props) {
  return (
    <div className="bg-white text-neutral-900 px-12 py-12 shadow-sm rounded-lg print:shadow-none print:rounded-none">
      {/* Header */}
      <div className="flex items-start justify-between gap-6 border-b-4 border-neutral-900 pb-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Service Agreement</h1>
          <p className="text-sm text-neutral-700 mt-1">6700 Orr Rd, Charlotte NC 28213</p>
          <p className="text-sm text-neutral-700">www.rbrestorationbuilders.com</p>
          <p className="text-sm text-neutral-700">(704) 284-5012</p>
        </div>
        {logoUrl && (
          <img src={logoUrl} alt="RB Restoration Builders" className="w-32 h-32 object-contain" />
        )}
      </div>

      {/* Homeowner */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
        <InlineField label="Homeowner:" value={data.homeowner} onChange={(v) => update("homeowner", v)} />
        <InlineField label="Phone #:" value={data.phone} onChange={(v) => update("phone", v)} />
        <InlineField label="Address:" value={data.address} onChange={(v) => update("address", v)} className="md:col-span-2" />
        <InlineField label="City:" value={data.city} onChange={(v) => update("city", v)} />
        <InlineField label="State/Zip:" value={data.state_zip} onChange={(v) => update("state_zip", v)} />
        <InlineField label="Email:" value={data.email} onChange={(v) => update("email", v)} type="email" className="md:col-span-2" />
        <InlineField label="Type of Loss:" value={data.type_of_loss} onChange={(v) => update("type_of_loss", v)} />
        <InlineField label="Claim #:" value={data.claim_number} onChange={(v) => update("claim_number", v)} />
      </div>

      {/* Contract subject to */}
      <SectionTitle>Contract subject to:</SectionTitle>
      <div className="space-y-2">
        <CheckRow
          label="Insurance Approval"
          checked={data.scope_insurance_approval}
          onChange={(v) => update("scope_insurance_approval", v)}
        />
        <CheckRow
          label="Price subject to full Ins. Scope"
          checked={data.scope_price_subject}
          onChange={(v) => update("scope_price_subject", v)}
        />
        <CheckRow
          label="Retail and/or Finance"
          checked={data.scope_retail_finance}
          onChange={(v) => update("scope_retail_finance", v)}
        />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pl-7 pt-2">
          <InlineField label="Total Price: $" value={data.total_price} onChange={(v) => update("total_price", v)} />
          <InlineField label="Deposit: $" value={data.deposit} onChange={(v) => update("deposit", v)} />
          <InlineField label="Months:" value={data.monthly_count} onChange={(v) => update("monthly_count", v)} />
          <InlineField label="× $" value={data.monthly_amount} onChange={(v) => update("monthly_amount", v)} />
        </div>
      </div>

      <p className="text-sm text-neutral-800 mt-5 leading-relaxed">
        I authorize <strong>RB Restoration Builders</strong> Representative to enter my property, and where
        applicable, furnish materials, supply all equipment and perform all labor necessary to perform the
        below listed Specified Services:
      </p>

      {/* Services grid */}
      <SectionTitle>Specified Services</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {/* Reconstruction */}
        <div className="space-y-2">
          <CheckRow
            label="Reconstruction of the Dwelling, Roof, and/or Other Structures"
            checked={data.svc_reconstruction}
            onChange={(v) => update("svc_reconstruction", v)}
          />
          <div className="grid grid-cols-2 gap-2 pl-7">
            <InlineField label="Roof SQ:" value={data.recon_roof_sq} onChange={(v) => update("recon_roof_sq", v)} />
            <InlineField label="Shed/Other SQ:" value={data.recon_shed_sq} onChange={(v) => update("recon_shed_sq", v)} />
            <InlineField label="Brand:" value={data.recon_brand} onChange={(v) => update("recon_brand", v)} />
            <InlineField label="Color:" value={data.recon_color} onChange={(v) => update("recon_color", v)} />
            <InlineField label="Gutters LF:" value={data.recon_gutters_lf} onChange={(v) => update("recon_gutters_lf", v)} />
            <InlineField label="Downsp LF:" value={data.recon_downsp_lf} onChange={(v) => update("recon_downsp_lf", v)} />
            <InlineField label="Layers:" value={data.recon_layers} onChange={(v) => update("recon_layers", v)} />
            <InlineField label="OSB:" value={data.recon_osb} onChange={(v) => update("recon_osb", v)} />
            <InlineField label="Stories:" value={data.recon_stories} onChange={(v) => update("recon_stories", v)} />
          </div>
        </div>

        {/* Gutters */}
        <div className="space-y-2">
          <CheckRow
            label="Gutters & Downspouts"
            checked={data.svc_gutters}
            onChange={(v) => update("svc_gutters", v)}
          />
          <div className="grid grid-cols-2 gap-2 pl-7">
            <InlineField label="Measurements:" value={data.gutters_measurements} onChange={(v) => update("gutters_measurements", v)} className="col-span-2" />
            <InlineField label="Brand:" value={data.gutters_brand} onChange={(v) => update("gutters_brand", v)} />
            <InlineField label="Color:" value={data.gutters_color} onChange={(v) => update("gutters_color", v)} />
            <InlineField label="Gutters LF:" value={data.gutters_lf} onChange={(v) => update("gutters_lf", v)} />
            <InlineField label="Downsp LF:" value={data.gutters_downsp_lf} onChange={(v) => update("gutters_downsp_lf", v)} />
          </div>
        </div>

        {/* Water/Interior */}
        <div className="space-y-2">
          <CheckRow
            label="Water Damage Emergency Dry-out"
            checked={data.svc_water_dryout}
            onChange={(v) => update("svc_water_dryout", v)}
          />
          <CheckRow
            label="Interior Damage"
            checked={data.svc_interior}
            onChange={(v) => update("svc_interior", v)}
          />
          <div className="pl-7">
            <InlineField label="Notes:" value={data.interior_notes} onChange={(v) => update("interior_notes", v)} />
          </div>
        </div>

        {/* Windows */}
        <div className="space-y-2">
          <CheckRow
            label="Windows"
            checked={data.svc_windows}
            onChange={(v) => update("svc_windows", v)}
          />
          <div className="grid grid-cols-2 gap-2 pl-7">
            <InlineField label="How Many:" value={data.windows_count} onChange={(v) => update("windows_count", v)} />
            <InlineField label="Color:" value={data.windows_color} onChange={(v) => update("windows_color", v)} />
            <InlineField label="Grids:" value={data.windows_grids} onChange={(v) => update("windows_grids", v)} />
            <InlineField label="Brand:" value={data.windows_brand} onChange={(v) => update("windows_brand", v)} />
            <InlineField label="Doors:" value={data.windows_doors} onChange={(v) => update("windows_doors", v)} />
            <InlineField label="Sliding:" value={data.windows_sliding} onChange={(v) => update("windows_sliding", v)} />
            <InlineField label="French:" value={data.windows_french} onChange={(v) => update("windows_french", v)} />
          </div>
        </div>

        {/* Emergency mitigation */}
        <div className="space-y-2">
          <CheckRow
            label="Emergency Mitigation / Tarp / Tree Removal"
            checked={data.svc_emergency_mitigation}
            onChange={(v) => update("svc_emergency_mitigation", v)}
          />
        </div>

        {/* Siding */}
        <div className="space-y-2">
          <CheckRow
            label="Siding"
            checked={data.svc_siding}
            onChange={(v) => update("svc_siding", v)}
          />
          <div className="grid grid-cols-2 gap-2 pl-7">
            <InlineField label="Measurements:" value={data.siding_measurements} onChange={(v) => update("siding_measurements", v)} className="col-span-2" />
            <InlineField label="Color:" value={data.siding_color} onChange={(v) => update("siding_color", v)} />
            <InlineField label="Brand:" value={data.siding_brand} onChange={(v) => update("siding_brand", v)} />
            <div className="flex gap-3 col-span-2">
              <CheckRow label="D4" checked={data.siding_style_d4} onChange={(v) => update("siding_style_d4", v)} />
              <CheckRow label="Dutch lap" checked={data.siding_style_dutch} onChange={(v) => update("siding_style_dutch", v)} />
            </div>
            <InlineField label="Fanfold:" value={data.siding_fanfold} onChange={(v) => update("siding_fanfold", v)} />
            <InlineField label="House wrap:" value={data.siding_house_wrap} onChange={(v) => update("siding_house_wrap", v)} />
          </div>
        </div>
      </div>

      {/* Terms */}
      <SectionTitle>Terms</SectionTitle>
      <div className="text-xs text-neutral-800 leading-relaxed space-y-3">
        <p>
          By Signing this Agreement, I Understand that I am choosing RB Restoration Builders (contractor) to
          complete the necessary work according to Insurance scope to my property once an agreement has been
          met with my Insurance Company and/or any other third party. The Property Owner authorizes RB
          Restoration Builders to obtain labor and material in accordance with contract price and Property
          Owner authorizes Company to accomplish the replacement or repair. During the project, Property
          Owner agrees to pay Company any monies received from the insurance company and/or its lender(s),
          or any other third party, such as cost increases, supplements, and/or general contractor overhead
          and profit, when paid. We will not honor our warranty on agreements that are not paid in full.
        </p>
        <p>
          I hereby authorize and instruct direct payment of any benefits or proceeds for services rendered
          by Contractor to be made payable solely to Contractor and sent exclusively to Contractor's Address
          and to endorse all such pertinent checks, drafts, bills of exchange for deposit to the
          Contractor's account for credit against the specified services on my property which have been
          completed or which will be completed.
        </p>
        <p>
          I agree that any portion of work, deductible(s), betterment, depreciation, or additional work
          requested by me, or otherwise not covered by insurance, is ultimately my responsibility.
        </p>
        <p>
          A change order with the details and proof of the damage will be presented to the property owner
          for approval (or Insurance Company if applicable). After the damage has been reviewed and approved
          by the owner (or insurance company if applicable), the work will continue.
        </p>
        <div className="flex items-baseline gap-3 py-2">
          <InlineField
            label="Initial:"
            value={data.finance_initial}
            onChange={(v) => update("finance_initial", v)}
            className="max-w-[12rem]"
          />
          <p className="text-xs">
            I Understand that If I'm Choosing to finance or Pay out of pocket the work on my property will
            be subject to Total Price agreement between Property Owner and Contractor and (Finance Company
            Approval if applicable). I am choosing RB Restoration Builders to complete the necessary work
            and I Agree with all terms and conditions.
          </p>
        </div>
        <p>
          Should default be made in payment of this agreement, a lien will be placed on the property and
          charges will be added from the date of substantial completion at the maximum allowed by law. If
          placed in the hands of an attorney for collection, the "Property Owner" will pay all attorney and
          legal fees. <strong>North Carolina:</strong> Any person or company supplying labor or materials
          for this improvement to your property may file a lien against your property if that person or
          company is not paid for their contributions.
        </p>
        <p className="font-semibold">
          As the Property Owner(s), I/We agree to all terms and conditions described in the front and back
          of this Service Agreement.
        </p>
      </div>

      {/* Signatures */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
        <div>
          <Label className="text-sm font-semibold">Homeowner Signature</Label>
          <SignaturePad
            value={data.homeowner_signature}
            onChange={(v) => update("homeowner_signature", v)}
          />
          <InlineField
            label="Date:"
            type="date"
            value={data.homeowner_sign_date}
            onChange={(v) => update("homeowner_sign_date", v)}
            className="mt-2"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold">Representative Signature</Label>
          <SignaturePad
            value={data.rep_signature}
            onChange={(v) => update("rep_signature", v)}
          />
          <InlineField
            label="Date:"
            type="date"
            value={data.rep_sign_date}
            onChange={(v) => update("rep_sign_date", v)}
            className="mt-2"
          />
        </div>
      </div>

      <p className="text-xs text-neutral-700 italic mt-6 border-t pt-3">
        *** This agreement is subject to the insurance company approval. If it's NOT approved, then the
        Homeowner will NOT have to pay anything out of pocket. IF APPROVED, then the Homeowner is ONLY
        responsible for the full deductible. ***
      </p>
    </div>
  );
}

// Re-import here to avoid circulars
import { Label } from "@/components/ui/label";
